// FinlyBooks Ledger Engine — hidden double-entry auto-posting
// Every transaction auto-posts to two ledgers (debit + credit) behind the scenes.

// Top-level account types
export const LEDGER_GROUPS = {
  ASSET:     { label: 'Assets',      color: '#0f6e56', bg: '#e1f5ee', normal: 'debit' },
  LIABILITY: { label: 'Liabilities', color: '#a32d2d', bg: '#fcebeb', normal: 'credit' },
  EQUITY:    { label: 'Equity',      color: '#534ab7', bg: '#eeedfb', normal: 'credit' },
  INCOME:    { label: 'Income',      color: '#185fa5', bg: '#e6f1fb', normal: 'credit' },
  EXPENSE:   { label: 'Expenses',    color: '#854f0b', bg: '#faeeda', normal: 'debit' },
};

// Account heads (sub-groups) — like Tally. Each belongs to a top-level group.
export const ACCOUNT_HEADS = [
  // Assets
  { head: 'Bank Accounts',        group: 'ASSET' },
  { head: 'Cash in Hand',         group: 'ASSET' },
  { head: 'Sundry Debtors',       group: 'ASSET' },
  { head: 'Stock / Inventory',    group: 'ASSET' },
  { head: 'Fixed Assets',         group: 'ASSET' },
  { head: 'Loans & Advances (Asset)', group: 'ASSET' },
  // Liabilities
  { head: 'Sundry Creditors',     group: 'LIABILITY' },
  { head: 'Loans (Liability)',    group: 'LIABILITY' },
  { head: 'Duties & Taxes',       group: 'LIABILITY' },
  { head: 'Current Liabilities',  group: 'LIABILITY' },
  // Equity
  { head: 'Capital Account',      group: 'EQUITY' },
  { head: 'Reserves & Surplus',   group: 'EQUITY' },
  // Income
  { head: 'Sales Account',        group: 'INCOME' },
  { head: 'Direct Income',        group: 'INCOME' },
  { head: 'Indirect Income',      group: 'INCOME' },
  // Expenses
  { head: 'Purchase Account',     group: 'EXPENSE' },
  { head: 'Direct Expenses',      group: 'EXPENSE' },
  { head: 'Indirect Expenses',    group: 'EXPENSE' },
];

// Get account heads available for a given group
export function headsForGroup(group) {
  return ACCOUNT_HEADS.filter(h => h.group === group);
}

// Get the group for a given head
export function groupForHead(head) {
  const found = ACCOUNT_HEADS.find(h => h.head === head);
  return found ? found.group : 'ASSET';
}

// Pre-built chart of accounts for small retail businesses (with account heads)
export const DEFAULT_LEDGERS = [
  { name: 'Cash on Hand',        head: 'Cash in Hand',      group: 'ASSET',     system: true },
  { name: 'Bank Account',        head: 'Bank Accounts',     group: 'ASSET',     system: true },
  { name: 'Accounts Receivable', head: 'Sundry Debtors',    group: 'ASSET',     system: true },
  { name: 'Inventory',           head: 'Stock / Inventory', group: 'ASSET',     system: true },
  { name: 'Accounts Payable',    head: 'Sundry Creditors',  group: 'LIABILITY', system: true },
  { name: 'Loans Payable',       head: 'Loans (Liability)', group: 'LIABILITY', system: true },
  { name: 'Tax Payable',         head: 'Duties & Taxes',    group: 'LIABILITY', system: true },
  { name: 'Forex Gain/Loss',     head: 'Indirect Income',   group: 'INCOME',    system: true },
  { name: "Owner's Capital",     head: 'Capital Account',   group: 'EQUITY',    system: true },
  { name: 'Retained Earnings',   head: 'Reserves & Surplus',group: 'EQUITY',    system: true },
  { name: 'Sales',               head: 'Sales Account',     group: 'INCOME',    system: true },
  { name: 'Other Income',        head: 'Indirect Income',   group: 'INCOME',    system: true },
  { name: 'Purchases (COGS)',    head: 'Purchase Account',  group: 'EXPENSE',   system: true },
  { name: 'Rent',                head: 'Indirect Expenses', group: 'EXPENSE',   system: true },
  { name: 'Utilities',           head: 'Indirect Expenses', group: 'EXPENSE',   system: true },
  { name: 'Salaries',            head: 'Indirect Expenses', group: 'EXPENSE',   system: true },
  { name: 'Transport',           head: 'Direct Expenses',   group: 'EXPENSE',   system: true },
  { name: 'Marketing',           head: 'Indirect Expenses', group: 'EXPENSE',   system: true },
  { name: 'Sundry Expenses',     head: 'Indirect Expenses', group: 'EXPENSE',   system: true },
];

function expenseLedger(category) {
  const map = {
    'Food & Supplies': 'Purchases (COGS)',
    'Rent': 'Rent', 'Utilities': 'Utilities', 'Salaries': 'Salaries',
    'Transport': 'Transport', 'Marketing': 'Marketing',
    'Equipment': 'Sundry Expenses', 'Other': 'Sundry Expenses',
  };
  return map[category] || 'Sundry Expenses';
}

export function generatePostings({ invoices = [], expenses = [], pettycash = [], cashbook = [], bankbook = [], journal = [], receipts = [], payments = [] }) {
  const posts = [];

  invoices.forEach(i => {
    const amt = Number(i.amount) || 0;        // net total the party owes (fare - discount + tax)
    const tax = Number(i.taxAmount) || 0;     // tax portion (0 for older/non-travel invoices)
    const base = Math.max(0, amt - tax);      // revenue/expense portion, net of discount
    if (i.type === 'sales') {
      // Sale posts on credit to Receivable; cash comes via Receipt voucher.
      // Revenue (net of discount) to Sales; output tax to Tax Payable liability.
      posts.push({ date: i.date, ref: i.invoiceNo, narration: 'Sales — ' + (i.customerName || ''), debit: 'Accounts Receivable', credit: 'Sales', amount: base });
      if (tax > 0) posts.push({ date: i.date, ref: i.invoiceNo, narration: 'Output tax — ' + (i.customerName || ''), debit: 'Accounts Receivable', credit: 'Tax Payable', amount: tax });
    } else if (i.type === 'purchase') {
      posts.push({ date: i.date, ref: i.invoiceNo, narration: 'Purchase — ' + (i.supplierName || ''), debit: 'Purchases (COGS)', credit: 'Accounts Payable', amount: base });
      if (tax > 0) posts.push({ date: i.date, ref: i.invoiceNo, narration: 'Input tax — ' + (i.supplierName || ''), debit: 'Tax Payable', credit: 'Accounts Payable', amount: tax });
    }
  });

  // Receipt voucher — money received from customer: Dr Cash/Bank, Cr Accounts Receivable
  receipts.forEach(r => {
    const amt = Number(r.amount) || 0;                 // base cash actually received
    const arRelief = (r.arRelief != null) ? Number(r.arRelief) : amt; // base A/R cleared (foreign x invoice-rate)
    const intoLedger = r.method === 'bank' ? 'Bank Account' : 'Cash on Hand';
    posts.push({ date: r.date, ref: r.voucherNo || 'RCP', narration: 'Receipt — ' + (r.partyName || ''), debit: intoLedger, credit: 'Accounts Receivable', amount: arRelief });
    const forex = amt - arRelief;                      // +ve = received more base than booked = gain
    if (Math.abs(forex) > 0.005) {
      if (forex > 0) posts.push({ date: r.date, ref: r.voucherNo || 'RCP', narration: 'Forex gain — ' + (r.partyName || ''), debit: intoLedger, credit: 'Forex Gain/Loss', amount: forex });
      else posts.push({ date: r.date, ref: r.voucherNo || 'RCP', narration: 'Forex loss — ' + (r.partyName || ''), debit: 'Forex Gain/Loss', credit: intoLedger, amount: -forex });
    }
  });

  // Payment voucher — money paid to supplier: Dr Accounts Payable, Cr Cash/Bank
  payments.forEach(p => {
    const amt = Number(p.amount) || 0;                 // base cash actually paid
    const apRelief = (p.apRelief != null) ? Number(p.apRelief) : amt; // base A/P cleared (foreign x invoice-rate)
    const fromLedger = p.method === 'bank' ? 'Bank Account' : 'Cash on Hand';
    posts.push({ date: p.date, ref: p.voucherNo || 'PMT', narration: 'Payment — ' + (p.partyName || ''), debit: 'Accounts Payable', credit: fromLedger, amount: amt });
    const forex = apRelief - amt;                      // +ve = cleared more base debt than cash paid = gain
    if (Math.abs(forex) > 0.005) {
      if (forex > 0) posts.push({ date: p.date, ref: p.voucherNo || 'PMT', narration: 'Forex gain — ' + (p.partyName || ''), debit: 'Accounts Payable', credit: 'Forex Gain/Loss', amount: forex });
      else posts.push({ date: p.date, ref: p.voucherNo || 'PMT', narration: 'Forex loss — ' + (p.partyName || ''), debit: 'Forex Gain/Loss', credit: 'Accounts Payable', amount: -forex });
    }
  });

  expenses.forEach(e => {
    const amt = Number(e.amount) || 0;
    posts.push({ date: e.date, ref: 'EXP', narration: e.description || 'Expense', debit: expenseLedger(e.category), credit: 'Cash on Hand', amount: amt });
  });

  pettycash.forEach(p => {
    const amt = Number(p.amount) || 0;
    if (p.type === 'in') posts.push({ date: p.date, ref: 'PC', narration: p.description || 'Cash received', debit: 'Cash on Hand', credit: "Owner's Capital", amount: amt });
    else posts.push({ date: p.date, ref: 'PC', narration: p.description || 'Cash paid', debit: 'Sundry Expenses', credit: 'Cash on Hand', amount: amt });
  });

  cashbook.forEach(c => {
    const amt = Number(c.amount) || 0;
    if (c.type === 'in') posts.push({ date: c.date, ref: 'CB', narration: c.description || 'Cash in', debit: 'Cash on Hand', credit: 'Other Income', amount: amt });
    else posts.push({ date: c.date, ref: 'CB', narration: c.description || 'Cash out', debit: 'Sundry Expenses', credit: 'Cash on Hand', amount: amt });
  });

  bankbook.forEach(b => {
    const amt = Number(b.amount) || 0;
    if (b.type === 'deposit') posts.push({ date: b.date, ref: 'BB', narration: b.description || 'Bank deposit', debit: 'Bank Account', credit: 'Cash on Hand', amount: amt });
    else posts.push({ date: b.date, ref: 'BB', narration: b.description || 'Bank withdrawal', debit: 'Cash on Hand', credit: 'Bank Account', amount: amt });
  });

  journal.forEach(j => {
    const amt = Number(j.amount) || 0;
    posts.push({ date: j.date, ref: 'JV', narration: j.narration || 'Journal entry', debit: j.debitLedger, credit: j.creditLedger, amount: amt });
  });

  return posts;
}

// Helper: compute outstanding balance per invoice from allocations
export function invoiceOutstanding(invoice, receipts = [], payments = []) {
  const vouchers = invoice.type === 'sales' ? receipts : payments;
  let allocated = 0;
  vouchers.forEach(v => {
    (v.allocations || []).forEach(a => {
      if (a.invoiceId === invoice.id) allocated += Number(a.amount) || 0;
    });
  });
  return Math.max(0, (Number(invoice.amount) || 0) - allocated);
}

// Compute settlement status for an invoice
export function invoiceSettlement(invoice, receipts = [], payments = []) {
  const outstanding = invoiceOutstanding(invoice, receipts, payments);
  const total = Number(invoice.amount) || 0;
  const paid = total - outstanding;
  let status = 'unpaid';
  if (outstanding <= 0.01) status = 'paid';
  else if (paid > 0.01) status = 'partial';
  return { outstanding, paid, total, status };
}

export function computeLedgerBalances(data, customLedgers = []) {
  const allLedgers = [...DEFAULT_LEDGERS, ...customLedgers];
  const balances = {};
  allLedgers.forEach(l => { balances[l.name] = { ...l, debit: 0, credit: 0 }; });

  const posts = generatePostings(data);
  posts.forEach(p => {
    if (!balances[p.debit]) balances[p.debit] = { name: p.debit, group: 'EXPENSE', head: 'Indirect Expenses', debit: 0, credit: 0 };
    if (!balances[p.credit]) balances[p.credit] = { name: p.credit, group: 'INCOME', head: 'Indirect Income', debit: 0, credit: 0 };
    balances[p.debit].debit += p.amount;
    balances[p.credit].credit += p.amount;
  });

  Object.values(balances).forEach(b => {
    const net = b.debit - b.credit;
    b.balance = Math.abs(net);
    b.balanceSide = net >= 0 ? 'debit' : 'credit';
    b.net = net;
  });

  return balances;
}

export function getLedgerEntries(data, ledgerName) {
  const posts = generatePostings(data);
  return posts
    .filter(p => p.debit === ledgerName || p.credit === ledgerName)
    .map(p => ({
      date: p.date, ref: p.ref, narration: p.narration,
      debit: p.debit === ledgerName ? p.amount : 0,
      credit: p.credit === ledgerName ? p.amount : 0,
      contra: p.debit === ledgerName ? p.credit : p.debit,
    }))
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
}
